import { HttpError } from '@pankod/refine-core';
import {
  Form,
  Input,
  Row,
  Col,
  Card,
  useForm,
  Edit,
  ListButton,
  Table,
  Typography,
  List,
  Select,
} from '@pankod/refine-antd';
import { useEffect, useState } from 'react';
import { IDamageGood } from 'interfaces';
import { getUsers } from 'services/user';
import styles from './style.module.css';
import { IDamagedProduct, IOption } from './create';

const { Option } = Select;
const { Text } = Typography;

export const DamageEdit: React.FC = () => {
  const [products, setProducts] = useState<Omit<IDamagedProduct, 'sku'>[]>([]);
  const [users, setUsers] = useState<IOption[]>([]);

  const { form, formProps, saveButtonProps, queryResult } = useForm<IDamageGood, HttpError>({
    metaData: {
      fields: [
        'id',
        'damageDate',
        'damageDetails',
        'totalAmount',
        {
          user: ['id', 'name'],
        },
        {
          warehouse: ['id', 'name'],
        },
        {
          damageProducts: [
            'id',
            'quantity',
            'lossAmount',
            {
              product: ['id', 'name'],
            },
          ],
        },
      ],
    },
  });

  const userId = queryResult?.data?.data?.user?.id;
  const warehouseId = queryResult?.data?.data?.warehouse?.id;
  const warehouseName = queryResult?.data?.data?.warehouse?.name;
  const damageDetails = queryResult?.data?.data?.damageDetails;
  const allProducts = queryResult?.data?.data?.damageProductsCopy?.map((item) => {
    return {
      id: item.productVariant.id,
      name: item.productVariant.name,
      quantity: item.quantity,
      lossAmount: item.lossAmount,
    };
  });

  useEffect(() => {
    (async () => {
      try {
        const usersResponse = await getUsers();
        const usersData = usersResponse.data.map((user) => {
          return {
            id: user.id,
            name: user.name,
          };
        });

        setUsers(usersData);
      } catch (_) {
        setUsers([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (products.length === 0 && allProducts?.length) {
      setProducts(allProducts as any);
    }
  }, [allProducts, products]);

  const getOptions = (items: IOption[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onFinish = (values: any) => {
    const data = {
      user: values.userId as string,
      damageDetails: values.damageDetails as string,
    };

    return (
      formProps.onFinish &&
      formProps.onFinish({
        ...data,
      })
    );
  };

  const footer = () => {
    const subTotal = products.reduce((total, product) => {
      total += Number(product.lossAmount);
      return total;
    }, 0);

    return (
      <div className={styles['product-footer']}>
        <Text>Sub-Total:-</Text>
        <Text>{subTotal}</Text>
      </div>
    );
  };

  return (
    <Edit
      isLoading={queryResult?.isFetching}
      saveButtonProps={{
        ...saveButtonProps,
        disabled: !products.length,
      }}
      headerProps={{ extra: <ListButton /> }}
    >
      <Form
        {...formProps}
        layout="vertical"
        initialValues={{
          userId,
          warehouseId,
          warehouseName,
          damageDetails,
        }}
        form={form}
        onFinish={onFinish}
      >
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={18}>
            <Card>
              <Form.Item
                label="Reported By"
                name="userId"
                rules={[{ required: true, message: 'Please select a user' }]}
              >
                <Select
                  placeholder="Select a user"
                  filterOption={(input, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(users)}
                </Select>
              </Form.Item>
              <Form.Item label="Warehouse ID" name="warehouseId" style={{ display: 'none' }}>
                <Input disabled />
              </Form.Item>
              <Form.Item label="Warehouse" name="warehouseName">
                <Input disabled />
              </Form.Item>
              <Form.Item label="Note" name="damageDetails">
                <Input.TextArea rows={4} />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        {/* Products Table */}
        <Row justify="space-around" align="middle">
          <Col xs={24} lg={18}>
            <List
              title="Products"
              headerButtonProps={{
                extra: null,
              }}
            >
              <Table
                dataSource={products}
                rowKey="id"
                scroll={{ x: true }}
                footer={(_data) => footer()}
              >
                <Table.Column dataIndex="name" title="Product Name" />
                <Table.Column dataIndex="quantity" title="Quantity" />
                <Table.Column dataIndex="lossAmount" title="Loss Amount" />
              </Table>
            </List>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};
